import * as SVG from 'svg.js';

import { EventEmitter, Inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material';

import { Point } from 'app/controls/image-cropper/region';
import { ErrorDialogComponent
       } from 'app/dialogs/error-dialog/error-dialog.component';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { BoundingBox
       } from 'collage-designer/stage/placeholder/image-placeholder';
import { StageConstants } from 'core/constants';
import { TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitter } from 'core/utils/async-event-emitter';
import { ImageProvider } from './imageprovider';

export interface GalleryImage
{
  readonly url: string;
  readonly width: number;
  readonly height: number;
  readonly id: string;
  readonly hash?: string;
  readonly defaultCrop?: BoundingBox;
  contentCenter: Point | undefined;
  readonly expirationDate?: Date;
}

class DesignerExpiredError extends TrivialError
{
  // i18n.error.designerExpiredError.title
  // i18n.error.designerExpiredError.message
  protected _name: string = 'DesignerExpiredError';
}

@Injectable()
export class GalleryService implements OnDestroy
{
  public newImageAvailable: AsyncEventEmitter<GalleryImage> =
      new AsyncEventEmitter();
  public imageStockChanged: EventEmitter<boolean> = new EventEmitter();
  public uploadProgressChanged: EventEmitter<boolean> = new EventEmitter();
  public uploadFailed: EventEmitter<SVG.Doc> = new EventEmitter();

  private images: Array<GalleryImage> = [];
  private draggingImage: GalleryImage;
  private usedImages: {[designStateId: string]: Array<string>} = {};
  private selectedImage: GalleryImage = undefined;
  private imageExpiredWarningId?: number = undefined;
  private _uploadInProgress: boolean = false;

  public lastVisitedPage: number = 1;
  public imagesUploaded: number = 0;
  public imagesToUpload: number = 0;
  public uploadProgress: number = 0;

  constructor(
      private dialog: MatDialog,
      private loggingService: LoggingService,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(ErrorService) private errorService: ErrorService,
  )
  { }

  public ngOnDestroy(): void
  {
    if (this.imageExpiredWarningId) {
      clearInterval(this.imageExpiredWarningId);
      this.imageExpiredWarningId = undefined;
    }
  }

  private async imageExpiredWarning(): Promise<void>
  {
    this.loggingService.log(
        'GalleryService',
        Severity.NOTICE,
        'DesignerExpiredError because of expired image');
    this.dialog.open(ErrorDialogComponent, {
      data: {
        errors: [ new DesignerExpiredError() ],
      },
      panelClass: 'dialog-pane',
      disableClose: true,
    });
  }

  public get uploadPercentage(): number
  {
    if (this.imagesToUpload < 1) {
      return 0;
    }

    return ((this.imagesUploaded + this.uploadProgress)
            / this.imagesToUpload
            * 100);
  }

  public async addImage(info: GalleryImage): Promise<void>
  {
    // identische Bilder nicht mehrfach hinzufügen
    if (info === undefined ||
        info.id === undefined ||
        this.getImageById(info.id) !== undefined)
    {
      return;
    }

    this.images.push(info);

    if (!this.imageExpiredWarningId && info.expirationDate) {
      // Maximum timeout for setTimeout is 24.8 days
      const maxExpirationPeriod = 2073600000; /* 24d */

      const timeout = info.expirationDate.getTime() - new Date().getTime();

      if (timeout > 0 && timeout < maxExpirationPeriod) {
        this.imageExpiredWarningId = setTimeout(
            this.imageExpiredWarning.bind(this),
            timeout);
      }
    }
    await this.newImageAvailable.emit(info);
    this.imageStockChanged.emit();
  }

  public async addPendingImage(
    provider: ImageProvider, target?: SVG.Image, bbox?: BoundingBox):
      Promise<GalleryImage>
  {
    const id = this.getRandomGalleryId();

    if (target) {
      target.attr(StageConstants.ATTRIBUTE_PENDING, id);
    }

    const provided = await provider(id);
    const info = Object.assign({defaultCrop: bbox}, provided) as GalleryImage;

    await this.addImage(info);
    return info;
  }

  public getImages(unusedOnly: boolean = false): Array<GalleryImage>
  {
    if (unusedOnly) {
      return this.images.filter((image) => {
        return !this.isImageUsed(image.id);
      });
    }
    else {
      return this.images;
    }
  }

  public getImageById(imageId: string): GalleryImage
  {
    for (let image of this.images) {
      if (image.id === imageId) {
        return image;
      }
    }
    return undefined;
  }

  public setDraggingImage(draggingImageId: string): void
  {
    if (draggingImageId !== undefined) {
      this.draggingImage = this.getImageById(draggingImageId);
    }
    else {
      this.draggingImage = undefined;
    }
  }

  public getDraggingImage(): GalleryImage
  {
    return this.draggingImage;
  }

  public isImageUsed(imageId: string): boolean
  {
    let usedImages = this.usedImages[this.getGalleryImagesStoreHash()];
    if (usedImages === undefined) {
      return false;
    }
    return usedImages.indexOf(imageId) >= 0;
  }

  public isImageSelected(imageId: string): boolean
  {
    if (this.selectedImage && this.selectedImage.id === imageId &&
        !this.isImageUsed(imageId)) {
      return true;
    }
    return false;
  }

  public getSelectedImage(): GalleryImage
  {
    return this.selectedImage;
  }

  public setSelectedImage(galleryImageOrId: GalleryImage | string): void
  {
    this.selectedImage = typeof(galleryImageOrId) === 'string'
        ? this.getImageById(galleryImageOrId) : galleryImageOrId;
  }

  public setImageAsUsed(imageId: string): void
  {
    let usedImages = this.usedImages[this.getGalleryImagesStoreHash()];
    if (usedImages === undefined) {
      this.usedImages[this.getGalleryImagesStoreHash()] = [];
    }
    this.usedImages[this.getGalleryImagesStoreHash()].push(imageId);
  }

  public setImageAsUnused(imageId: string): void
  {
    let usedImages = this.usedImages[this.getGalleryImagesStoreHash()];

    let index = usedImages.indexOf(imageId);
    if (index > -1) {
      this.usedImages[this.getGalleryImagesStoreHash()].splice(index, 1);
    }
  }

  public setImagesAsUnused(): void
  {
    this.usedImages[this.getGalleryImagesStoreHash()] = [];
  }

  public removeImage(imageId: string): void
  {
    this.setImageAsUnused(imageId);
    this.images = this.images.filter((image) => {
      return image.id !== imageId;
    });
    this.imageStockChanged.emit();
  }

  public beginUploadImages(): void
  {
    this.uploadInProgress = true;
  }

  public finishUploadImages(): void
  {
    this.errorService.flush('GalleryService');
    if (this.imagesToUpload <= this.imagesUploaded) {
      this.uploadInProgress = false;
      this.imagesToUpload = 0;
      this.imagesUploaded = 0;
    }
  }

  public imageUploadFailed(error: TrivialError, target?: any): void
  {
    if (target) {
      this.uploadFailed.emit(target.parent());
    }
    this.errorService.add(
        error, 'GalleryService', !(error.name === 'UnsupportedMediaTypeError'));
  }

  public flushFailedUploads(): void
  {
    this.errorService.flushByType('GalleryService');
  }

  public get uploadInProgress(): boolean
  {
    return this._uploadInProgress;
  }

  public set uploadInProgress(progress: boolean)
  {
    this._uploadInProgress = progress;
    this.uploadProgressChanged.emit(progress);
  }

  private getGalleryImagesStoreHash(): string
  {
    let currentDesignState = this.designStepService.getCurrentDesignState();
    return [
        currentDesignState.designStepId,
        currentDesignState.partId
    ].join('-');
  }

  private getRandomGalleryId(): string
  {
    return 'gi' + Utils.getRandomId(24);
  }
}



// WEBPACK FOOTER //
// ./src/app/gallery/gallery.service.ts